import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import InfoTooltip from './InfoTooltip';

function normalizeOption(option) {
  if (typeof option === 'string') {
    return { value: option, label: option };
  }
  return {
    value: option.value,
    label: option.label || option.value
  };
}

function normalizeText(value = '') {
  return String(value || '').trim().toLowerCase();
}

const EMPTY_VALUES = [];

function CreatableDropdown({
  label,
  tooltip,
  value = '',
  values = EMPTY_VALUES,
  options = EMPTY_VALUES,
  onChange,
  multiple = false,
  placeholder = 'Select or type'
}) {
  const dropdownId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(multiple ? '' : value);
  const inputId = `${dropdownId}-input`;
  const menuId = `${dropdownId}-menu`;

  const normalizedOptions = useMemo(() => options.map(normalizeOption), [options]);
  const selectedValues = useMemo(() => (multiple ? values : EMPTY_VALUES), [multiple, values]);
  const selectedValueSet = useMemo(
    () => new Set(selectedValues.map((item) => normalizeText(item))),
    [selectedValues]
  );

  useEffect(() => {
    if (!multiple) {
      setInputValue(value || '');
    }
  }, [multiple, value]);

  const commitValue = (rawValue) => {
    const nextValue = String(rawValue || '').trim();
    if (!nextValue) {
      setInputValue('');
      return;
    }

    if (multiple) {
      if (!selectedValueSet.has(normalizeText(nextValue))) {
        onChange([...selectedValues, nextValue]);
      }
      setInputValue('');
      setOpen(false);
      return;
    }

    onChange(nextValue);
    setInputValue(nextValue);
    setOpen(false);
  };

  useEffect(() => {
    const onClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        if (multiple && inputValue.trim()) {
          commitValue(inputValue);
        }
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  });

  const filteredOptions = normalizedOptions
    .filter((option) => {
      if (multiple && selectedValueSet.has(normalizeText(option.value))) {
        return false;
      }
      const query = normalizeText(inputValue);
      if (!query) {
        return true;
      }
      return normalizeText(option.label).includes(query) || normalizeText(option.value).includes(query);
    })
    .slice(0, 10);

  const exactMatch = normalizedOptions.some((option) => (
    normalizeText(option.value) === normalizeText(inputValue) ||
    normalizeText(option.label) === normalizeText(inputValue)
  ));
  const canUseTypedValue = Boolean(inputValue.trim()) && (multiple
    ? !selectedValueSet.has(normalizeText(inputValue))
    : !exactMatch);

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setOpen(true);
    if (!multiple) {
      onChange(nextValue);
    }
  };

  const handleRemoveSelected = (itemToRemove) => {
    onChange(selectedValues.filter((item) => normalizeText(item) !== normalizeText(itemToRemove)));
  };

  return (
    <div className="creatable-dropdown" ref={rootRef}>
      {label && (
        <label className="input-label" htmlFor={inputId}>
          {label}
          {tooltip && <InfoTooltip text={tooltip} />}
        </label>
      )}
      <div className="creatable-input-shell">
        <input
          id={inputId}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitValue(inputValue);
            }
          }}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={open}
          aria-controls={menuId}
          aria-autocomplete="list"
        />
        <button
          type="button"
          className="creatable-menu-toggle"
          onClick={() => setOpen((previous) => !previous)}
          aria-label={open ? 'Close options' : 'Open options'}
        >
          <ChevronDown size={16} className={open ? 'dropdown-icon open' : 'dropdown-icon'} />
        </button>
      </div>

      {multiple && selectedValues.length > 0 && (
        <div className="creatable-selected-list" role="list" aria-label={`Selected ${label || 'options'}`}>
          {selectedValues.map((item) => (
            <span className="creatable-selected-chip" key={item}>
              {item}
              <button type="button" onClick={() => handleRemoveSelected(item)} aria-label={`Remove ${item}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="dropdown-menu creatable-menu" id={menuId} role="listbox" aria-labelledby={inputId}>
          {canUseTypedValue && (
            <button
              type="button"
              className="dropdown-item creatable-custom-option"
              role="option"
              aria-selected="false"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => commitValue(inputValue)}
            >
              {multiple ? `Add "${inputValue.trim()}"` : `Use "${inputValue.trim()}"`}
            </button>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={normalizeText(value) === normalizeText(option.value) ? 'dropdown-item selected' : 'dropdown-item'}
                role="option"
                aria-selected={normalizeText(value) === normalizeText(option.value)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => commitValue(option.value)}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="dropdown-empty">No matching options</div>
          )}
        </div>
      )}
    </div>
  );
}

export default CreatableDropdown;
